import React from 'react';
import Navigation from '../../components/tulote/Navigation';
import { 
  Building2, 
  MapPin, 
  Users, 
  Globe,
  TrendingUp,
  CheckCircle,
  Heart
} from 'lucide-react';
import { useCountry } from '../../contexts/CountryContext';
import { getUrlWithCountry } from '../../utils/countryUtils';

function AboutPage() {
  const { countryConfig, country } = useCountry();
  // Obtener el nombre del país desde la configuración, o usar 'Perú' como fallback
  const countryName = countryConfig?.name || 'Perú';
  
  // Helper function to get path with country parameter
  const getPath = (path) => {
    return getUrlWithCountry(path, country);
  };

  const features = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Mapas Interactivos',
      description: 'Explora proyectos inmobiliarios con mapas interactivos y visualización detallada de lotes disponibles.'
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: 'Proyectos Inmobiliarios',
      description: 'Accede a información completa de proyectos de vivienda de las mejores inmobiliarias del país.'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Acceso para Todos',
      description: 'Plataforma abierta que facilita el acceso a información inmobiliaria para todos en Perú y Latinoamérica.'
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Cobertura Regional',
      description: 'Proyectos inmobiliarios de diferentes regiones, facilitando la búsqueda sin importar tu ubicación.'
    }
  ];

  const benefits = [
    'Información centralizada de proyectos inmobiliarios',
    'Búsqueda fácil por ubicación y características',
    'Visualización interactiva de lotes y proyectos disponibles',
    'Acceso gratuito para todos los usuarios',
    'Información actualizada en tiempo real',
    'Múltiples inmobiliarias en un solo lugar'
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-10 h-10 text-orange-600 dark:text-orange-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Sobre Este Sitio
          </h1>
          <p className="text-xl sm:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            Plataforma inmobiliaria que muestra proyectos de vivienda de {countryName} y facilita el acceso a información inmobiliaria para todos.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 sm:p-12 border border-gray-200 dark:border-gray-700 mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Nuestra Misión
            </h2>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
              <strong className="text-orange-600 dark:text-orange-400">LotesPro.com</strong> es una plataforma diseñada para mostrar proyectos inmobiliarios de <strong>{countryName}</strong>. Actualmente, muchas inmobiliarias se están sumando a nuestra plataforma, agregando sus proyectos de vivienda para facilitar el acceso de todos en <strong>{countryName}</strong> y <strong>Latinoamérica</strong>.
            </p>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
              Nuestro objetivo es centralizar la información inmobiliaria, haciendo que sea más fácil para las personas encontrar y explorar proyectos de vivienda, sin importar dónde se encuentren. Creemos que el acceso a información inmobiliaria debe ser simple, transparente y disponible para todos.
            </p>
          </div>

          {/* Features Grid */}
          <div className="mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              ¿Qué Ofrecemos?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center text-orange-600 dark:text-orange-400 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="bg-gradient-to-br from-orange-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-xl shadow-lg p-8 sm:p-12 border border-gray-200 dark:border-gray-700 mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Beneficios para los Usuarios
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 dark:text-gray-300 text-lg">{benefit}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Growth Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 sm:p-12 border border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center text-orange-600 dark:text-orange-400 flex-shrink-0">
                <TrendingUp className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Crecimiento Constante
                </h2>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Cada día más inmobiliarias se unen a nuestra plataforma, expandiendo nuestra base de datos de proyectos inmobiliarios. Esto significa más opciones para ti y mejores oportunidades de encontrar el proyecto de vivienda perfecto.
                </p>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  Estamos comprometidos con el crecimiento continuo de la plataforma, agregando nuevas funcionalidades y mejorando la experiencia de usuario para hacer que la búsqueda de proyectos inmobiliarios sea aún más fácil y accesible.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-600 to-orange-500">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Únete a Nuestra Comunidad
          </h2>
          <p className="text-xl text-orange-50 mb-8">
            Si eres una inmobiliaria y quieres agregar tus proyectos, o si eres un usuario buscando tu próximo hogar, 
            estás en el lugar correcto.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={getPath('/como-agregar-proyectos')}
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-orange-600 rounded-xl font-semibold text-lg hover:bg-orange-50 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Building2 className="w-5 h-5 mr-2" />
              Agregar Mi Proyecto
            </a>
            <a
              href={getPath('/proyectos')}
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white border-2 border-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-all duration-200"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Explorar Proyectos
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;

